// Extracted from Settings.jsx for code splitting — keeps PlanGate out of lazy-loaded Settings chunk
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { usePlanLimits, useEntitlements } from './account'
import { authFetch } from './api'
import { PACK_CATALOG } from './packCatalog'
import { useIsTWA } from './useIsTWA'

/**
 * @typedef {'free' | 'starter' | 'pro' | 'enterprise'} PlanTier
 * @typedef {keyof typeof GATE_CONFIG} GateKey
 */

/**
 * @typedef {{ icon: string, title: string, requiredPlan: PlanTier, planLabel: string, description?: string }} TierGateEntry
 * @typedef {{ requiredPack: string }} PackGateEntry
 * @typedef {TierGateEntry | PackGateEntry} GateEntry
 */

/**
 * @typedef {Object} PlanGateProps
 * @property {string} gateKey - Key into GATE_CONFIG
 * @property {React.ReactNode} children
 */

/**
 * Narrow an untrusted string (e.g. from DB) to PlanTier. Falls back to 'free'
 * when the value is null/undefined or unrecognized — consistent with the
 * application's default-to-free policy.
 * @param {string | null | undefined} value
 * @returns {PlanTier}
 */
export function asPlanTier(value) {
  if (value === 'starter' || value === 'pro' || value === 'enterprise') return value
  return 'free'
}

/**
 * Resolve a nav-link `gate` key (unknown string) to its required PlanTier,
 * or null when the key is absent or not registered in GATE_CONFIG.
 * @param {string | null | undefined} gateKey
 * @returns {PlanTier | null}
 */
export function getRequiredPlan(gateKey) {
  if (!gateKey) return null
  const entry = /** @type {GateEntry | undefined} */ (/** @type {any} */ (GATE_CONFIG)[gateKey])
  if (!entry || !('requiredPlan' in entry)) return null
  return entry.requiredPlan
}

export const GATE_CONFIG = {
  branding:   { icon: 'settings-branding',   title: 'Custom Branding',            requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  automation: { icon: 'settings-automation', title: 'Automated Emails',           requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  invoices:   { icon: 'nav-invoices',        title: 'Invoicing',                  requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  contracts:  { icon: 'nav-contracts',       title: 'Contract Management',        requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  documents:  { icon: 'nav-documents',       title: 'Company Documents',          requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  expenses:   { icon: 'nav-expenses',        title: 'Expense Tracking',           requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  services:   { icon: 'settings-advanced',   title: 'Service Configuration',      requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  financials: { icon: 'nav-financials',      title: 'Financial Reports',          requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  utilization: { icon: 'nav-utilization',    title: 'Crew Utilization Analytics', requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  ai:         { icon: 'chrome-ai-chat',      title: 'AI Assistant',               requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  executive_report: { icon: 'auto-exec-report', title: 'Executive Reports',       requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  teams:       { icon: 'settings-team',      title: 'Team Management',            requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  portal:      { icon: 'portal-schedule',    title: 'Client Portal',              requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  leads:       { icon: 'action-new-leads',   title: 'Online Booking & Leads',     requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  forms:       { icon: 'nav-forms',          title: 'Forms & Checklists',         requiredPlan: 'starter', planLabel: 'Starter ($49/mo)' },
  approvals:   { icon: 'status-verified',    title: 'Approval Workflows',         requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  public_api:  { icon: 'settings-advanced',  title: 'REST API Access',            requiredPlan: 'pro',     planLabel: 'Pro ($99/mo)' },
  inventory:   { requiredPack: 'inventory_pack' },
}

/** @type {Record<PlanTier, number>} */
export const PLAN_ORDER = { free: 0, starter: 1, pro: 2, enterprise: 3 }

/**
 * @param {{ packKey: string }} props
 */
function PackUpgradeOverlay({ packKey }) {
  const pack = PACK_CATALOG[packKey]
  const isTWA = useIsTWA()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(/** @type {string | null} */ (null))

  const handleAddPack = async () => {
    if (isTWA) {
      window.open(`https://crewos.foundryos.ca/settings?tab=billing&addon=${encodeURIComponent(packKey)}`, '_blank', 'noopener,noreferrer')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch('/api/stripe-checkout', {
        method: 'POST',
        body: JSON.stringify({ action: 'pack_checkout', packKey, interval: 'monthly' }),
      })
      if (!res.ok) throw new Error('Checkout failed')
      const body = /** @type {{ url?: string }} */ (await res.json())
      if (!body.url) throw new Error('No checkout URL returned')
      window.location.href = body.url
    } catch {
      setError('Could not start checkout. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg gap-3 p-6">
        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
          <Lock size={24} className="text-gray-400" />
        </div>
        <div className="text-center">
          <p className="font-semibold text-gray-900">{pack?.label ?? 'Add-On Pack Required'}</p>
          {pack?.description && (
            <p className="text-sm text-gray-500 mt-1">{pack.description}</p>
          )}
          {isTWA && (
            <p className="text-xs text-gray-400 mt-2">Add-on packs are managed on the web.</p>
          )}
        </div>
        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
        <button
          onClick={handleAddPack}
          disabled={loading}
          className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Redirecting…' : isTWA ? 'Manage on Web →' : `Add ${pack?.label ?? 'Pack'}`}
        </button>
      </div>
    </div>
  )
}

/**
 * Feature gate — renders children only when the account plan meets the requirement,
 * or when the account has the required add-on pack entitlement.
 * @param {PlanGateProps} props
 */
export function PlanGate({ gateKey, children }) {
  const { plan } = usePlanLimits()
  const { hasPack } = useEntitlements()
  const navigate = useNavigate()
  const gate = /** @type {GateEntry | undefined} */ (/** @type {any} */ (GATE_CONFIG)[gateKey])
  if (!gate) return children

  const hasAccess = 'requiredPack' in gate
    ? hasPack(gate.requiredPack)
    : PLAN_ORDER[plan] >= PLAN_ORDER[gate.requiredPlan]

  if (hasAccess) return children

  if ('requiredPack' in gate) {
    return <PackUpgradeOverlay packKey={gate.requiredPack} />
  }

  return (
    <div className="relative">
      <div className="opacity-[0.15] pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      <div className="absolute inset-0 flex items-start justify-center pt-16">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{gate.title}</h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">{gate.description}</p>
          <div className="bg-brand-50 rounded-lg px-4 py-3 mb-6">
            <p className="text-xs text-brand-700 font-medium">
              Available on <span className="font-bold">{gate.planLabel}</span> and above
            </p>
          </div>
          <button
            onClick={() => navigate('/settings?tab=billing')}
            className="w-full px-6 py-3 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 transition-colors"
          >
            View Plans & Upgrade
          </button>
        </div>
      </div>
    </div>
  )
}
